<template>
  <DocSummaryPage
    :doc="episode"
    :isLoadingDoc="isLoadingDoc"
    :isErrorLoadingDoc="isErrorLoadingDoc"
    class="layout--podcast"
  />
</template>

<script setup lang="ts">
import { computed, onBeforeMount, Raw, ref } from 'vue'
import {
  onBeforeRouteLeave,
  onBeforeRouteUpdate,
  RouteLocationNormalized,
  useRoute,
  useRouter,
} from 'vue-router'
import { useWait } from '@/app/composables/useWait'

import { backend } from '@/services/backend'

import { Sentry } from '@/services/sentry'
import { assertNotNull } from '@/helpers/typing'

import { PodcastEpisodeView } from '@/models/doc.podcast.episode'
import { backLink } from '@/models/backlink'

import DocSummaryPage from '@/app/components/doc/DocSummaryPage.vue'

const route = useRoute()
const router = useRouter()
const wait = useWait()

const episode = ref<Raw<PodcastEpisodeView> | null>(null)

onBeforeRouteUpdate(
  async (
    to: RouteLocationNormalized,
    from: RouteLocationNormalized,
    next: any,
  ) => {
    const episodeValue = assertNotNull(episode.value)

    episodeValue.initPage(to, from, loadDocument)
    backLink.value.updateMetaTitle(from, episodeValue.title)

    next()
  },
)

onBeforeRouteLeave(
  async (
    to: RouteLocationNormalized,
    from: RouteLocationNormalized,
    next: any,
  ) => {
    // Called when the route that renders this component is about to
    // be navigated away from.
    // Has access to `this` component instance.
    if (episode.value) {
      await episode.value.updateReadTimeAndSavePosition()
      backLink.value.updateMetaTitle(from, episode.value.title)
    }
    next()
  },
)

onBeforeMount(async () => {
  await loadDocument(route)
})

async function _fetchDocument(
  route: RouteLocationNormalized,
): Promise<PodcastEpisodeView> {
  const url_slug = route.params.url_slug as string

  // TODO: implement offline mode for podcast episodes.
  // if (offline.isOffline) {
  //   await loadBookDetailFromNativeStorage(url_slug)
  // }

  const response = await backend.getPodcastEpisode(url_slug)
  const doc = new PodcastEpisodeView(response)

  // Use author from podcast instead of episode, see #11475
  if (response.doc_group && response.doc_group.author) {
    doc.author = response.doc_group.author
  }

  doc.getPageFromRoute(route)

  return doc
}

async function loadDocument(route: RouteLocationNormalized): Promise<void> {
  try {
    wait.start('Loading Doc')
    wait.end('Error Loading Doc')

    episode.value = await _fetchDocument(route)
    episode.value.initDocument(true)

    if (!episode.value.isPreviewPage) {
      await episode.value.scrollToTop()
    }
  } catch (error: any) {
    if (error.request && error.request.status === 404) {
      // Redirect to the non-existing page
      router.push({ name: 'not_found' })
    } else {
      wait.start('Error Loading Doc')
      Sentry.captureException(error)
    }
  } finally {
    wait.end('Loading Doc')
  }
}

const isLoadingDoc = computed(() => {
  return wait.is('Loading Doc')
})

const isErrorLoadingDoc = computed(() => {
  return wait.is('Error Loading Doc')
})
</script>
